import { useState, useEffect, useRef, useCallback } from "react";
import { updateWebsocketMessage } from "@modules/notify";

/**
 * WebSocket 연결을 관리하는 커스텀 훅
 * @param {string} url - WebSocket URL
 * @param {string} token - WebSocket 토큰 (이미 발급받은 토큰)
 * @param {Object} options - 추가 옵션들
 */
const useWebSocket = (url, token, options = {}) => {
    const { onMessage = () => {}, onOpen = () => {}, onClose = () => {}, onError = () => {}, enabled = true } = options;

    const [connectionStatus, setConnectionStatus] = useState("Disconnected");
    const [lastMessage, setLastMessage] = useState(null);
    const [error, setError] = useState(null);

    const ws = useRef(null);
    const isConnectingRef = useRef(false);
    const connectionAttemptRef = useRef(0);
    const lastConnectionParamsRef = useRef(null);

    // 하트비트 타이머 참조
    const heartbeatIntervalRef = useRef(null);

    // 자동 재연결 관련
    const reconnectTimeoutRef = useRef(null);
    const reconnectAttemptRef = useRef(0);
    const maxReconnectAttempts = 10; // 최대 재연결 시도 횟수
    const baseReconnectDelay = 1000; // 기본 재연결 지연 시간 (ms)

    // 메시지 전송 함수
    const sendMessage = useCallback((message) => {
        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
            try {
                const messageString = typeof message === "string" ? message : JSON.stringify(message);
                ws.current.send(messageString);
                console.log("Message sent:", messageString);
                return true;
            } catch (err) {
                console.error("Failed to send message:", err);
                setError("메시지 전송 실패");
                return false;
            }
        } else {
            console.warn("WebSocket is not connected");
            setError("WebSocket이 연결되지 않았습니다");
            return false;
        }
    }, []);

    /**
     * 하트비트 메시지를 2분마다 전송하는 함수
     */
    const startHeartbeat = useCallback(() => {
        // 기존 타이머 정리
        if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
        }

        console.log("Starting heartbeat (every 2 minutes)...");

        // 2분(120초)마다 하트비트 전송
        heartbeatIntervalRef.current = setInterval(
            () => {
                if (ws.current && ws.current.readyState === WebSocket.OPEN) {
                    console.log("Sending heartbeat...");
                    sendMessage({
                        action: "sendHeartBeat",
                        data: { status: "UP" },
                    });
                }
            },
            2 * 60 * 1000,
        ); // 2분
    }, [sendMessage]);

    /**
     * 하트비트 정지
     */
    const stopHeartbeat = useCallback(() => {
        if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = null;
            console.log("Heartbeat stopped");
        }
    }, []);

    // 재연결 시도 취소 함수
    const cancelReconnect = useCallback(() => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
        reconnectAttemptRef.current = 0;
    }, []);

    // WebSocket 연결 함수
    const connect = useCallback(() => {
        if (!url || !token || !enabled) {
            console.log("Connect cancelled: missing parameters", { url: !!url, token: !!token, enabled });
            return;
        }

        const currentParams = `${url}|${token}`;

        if (isConnectingRef.current) {
            console.log("Connect cancelled: already connecting");
            return;
        }

        if (
            ws.current &&
            ws.current.readyState === WebSocket.OPEN &&
            lastConnectionParamsRef.current === currentParams
        ) {
            console.log("Connect cancelled: already connected with same parameters");
            return;
        }

        const attemptNumber = ++connectionAttemptRef.current;
        console.log(`Starting connection attempt #${attemptNumber}`, { url, tokenLength: token.length });

        if (ws.current) {
            console.log("Closing existing connection");
            ws.current.close();
            ws.current = null;
        }

        try {
            isConnectingRef.current = true;
            lastConnectionParamsRef.current = currentParams;
            setConnectionStatus("Connecting");
            setError(null);

            console.log(`Creating WebSocket connection #${attemptNumber}`);

            // WebSocket 토큰을 쿼리 파라미터로 전달
            const finalUrl = `${url}?authorization=${encodeURIComponent(token)}`;
            console.log("🔗 WebSocket Connection Debug:", {
                baseUrl: url,
                tokenLength: token.length,
                tokenPreview: token.substring(0, 30) + "...",
                encodedToken: encodeURIComponent(token).substring(0, 30) + "...",
                finalUrl: finalUrl,
            });

            ws.current = new WebSocket(finalUrl);

            ws.current.onopen = (event) => {
                if (connectionAttemptRef.current !== attemptNumber) {
                    console.log(`Connection #${attemptNumber} ignored: newer attempt in progress`);
                    return;
                }

                console.log(`WebSocket connected #${attemptNumber} successfully`);

                // 재연결 성공 시 재연결 시도 횟수 리셋
                reconnectAttemptRef.current = 0;
                cancelReconnect();

                // 하트비트 시작
                startHeartbeat();

                isConnectingRef.current = false;
                setConnectionStatus("Connected");
                setError(null);
                lastConnectionParamsRef.current = currentParams;
                onOpen(event);
            };

            ws.current.onmessage = (event) => {
                try {
                    const response = JSON.parse(event.data);
                    console.log("WebSocket message received:", response);

                    // 응답 구조: { message: "success", data: {...}, function: "..." }
                    const { action: functionName, data } = response;

                    // 각 함수별 처리
                    if (functionName === "fetchMessages") {
                        // 메시지 목록 조회 응답
                        console.log("Received message list:", data.results.length, "messages");
                        onMessage({
                            type: "MESSAGE_LIST",
                            data: data.results,
                            nextToken: data.nextToken,
                        });
                    } else if (functionName === "putMessageStatus") {
                        // 메시지 상태 변경 응답
                        console.log("Message status updated successfully");
                        onMessage({
                            type: "STATUS_UPDATED",
                        });
                    } else if (functionName === "sendHeartBeat") {
                        // 하트비트 응답 (무시)
                        console.log("Heartbeat acknowledged");
                    } else if (functionName === "notify") {
                        // 📢 실시간 notify 알림
                        console.log("📢 Received NOTIFY:", data);
                        onMessage({
                            type: "NOTIFY",
                            data: data,
                        });
                    } else if (functionName === "sessionChatReply") {
                        // AI 분석 Chat 응답
                        onMessage({
                            type: "SESSION_CHAT_REPLY",
                            data: data,
                        });
                    } else {
                        // 예상치 못한 응답
                        console.log("Received unexpected message:", functionName);
                        setLastMessage(response);
                    }
                } catch (err) {
                    console.error("Failed to parse WebSocket message:", err, "Raw data:", event.data);
                    const rawMessage = {
                        raw: event.data,
                        timestamp: new Date().toISOString(),
                        type: "RAW",
                    };
                    setLastMessage(rawMessage);
                }
            };

            ws.current.onclose = (event) => {
                if (connectionAttemptRef.current !== attemptNumber) {
                    console.log(`Connection close #${attemptNumber} ignored: newer attempt in progress`);
                    return;
                }

                const timestamp = new Date().toISOString();
                const closeInfo = {
                    code: event.code,
                    reason: event.reason,
                    wasClean: event.wasClean,
                    timestamp,
                };

                console.error(`❌ WebSocket disconnected #${attemptNumber}:`, closeInfo);

                // 하트비트 정지
                stopHeartbeat();

                const getCloseReason = (code) => {
                    const reasons = {
                        1001: "Going away",
                        1006: "Abnormal closure - 서버 연결 거부 또는 네트워크 오류",
                    };
                    return reasons[code] || `Unknown error (${code})`;
                };

                const closeReason = getCloseReason(event.code);
                console.error(`❌ Close reason: ${closeReason}`);

                // 에러 메시지 설정 (사용자에게 표시)
                let errorMessage = null;
                if (event.code === 1006) {
                    errorMessage =
                        "❌ 연결 실패 (Code 1006)\n서버가 연결을 거부했습니다.\n\n확인 사항:\n1. WebSocket URL이 정확한가?\n2. 토큰이 유효한가?\n3. API Gateway 설정이 올바른가?";
                } else if (event.code !== 1001) {
                    errorMessage = `❌ 연결 종료 (Code ${event.code})\n${closeReason}`;
                }

                if (errorMessage) {
                    console.error("📢 Error message:", errorMessage);
                    setError(errorMessage);
                }

                isConnectingRef.current = false;
                setConnectionStatus("Disconnected");
                ws.current = null;
                lastConnectionParamsRef.current = null;
                onClose(event);

                // 비정상 종료인 경우 자동 재연결 시도
                // code 1000 (정상 종료) 또는 1001 (Going away)는 재연결하지 않음
                if (enabled && url && token && event.code !== 1000 && event.code !== 1001) {
                    console.log("🔄 Connection closed abnormally, attempting auto-reconnect...");
                    // onclose 내부에서 직접 재연결 로직 처리 (순환 참조 방지)
                    if (reconnectAttemptRef.current >= maxReconnectAttempts) {
                        console.error(
                            `❌ Max reconnect attempts (${maxReconnectAttempts}) reached. Stopping auto-reconnect.`,
                        );
                        setError("자동 재연결 실패: 최대 시도 횟수 초과");
                        reconnectAttemptRef.current = 0;
                    } else {
                        reconnectAttemptRef.current += 1;
                        const delay = baseReconnectDelay * Math.pow(2, reconnectAttemptRef.current - 1);
                        console.log(`🔄 Attempting reconnect #${reconnectAttemptRef.current} in ${delay}ms...`);
                        reconnectTimeoutRef.current = setTimeout(() => {
                            if (enabled && url && token) {
                                connect();
                            }
                        }, delay);
                    }
                } else {
                    // 정상 종료인 경우 재연결 시도 횟수 리셋
                    reconnectAttemptRef.current = 0;
                }
            };

            ws.current.onerror = (event) => {
                if (connectionAttemptRef.current !== attemptNumber) {
                    console.log(`Connection error #${attemptNumber} ignored: newer attempt in progress`);
                    return;
                }

                console.error(`WebSocket error #${attemptNumber}:`, event);
                isConnectingRef.current = false;
                setError("연결 오류 - 네트워크 또는 서버 설정을 확인해주세요");
                setConnectionStatus("Error");
                lastConnectionParamsRef.current = null;
                onError(event);
            };
        } catch (err) {
            console.error(`Failed to create WebSocket connection #${attemptNumber}:`, err);
            isConnectingRef.current = false;
            setError(`연결 생성 실패: ${err.message}`);
            setConnectionStatus("Error");
            lastConnectionParamsRef.current = null;
        }
    }, [
        url,
        token,
        enabled,
        onMessage,
        onOpen,
        onClose,
        onError,
        startHeartbeat,
        stopHeartbeat,
        cancelReconnect,
        maxReconnectAttempts,
        baseReconnectDelay,
    ]);

    // WebSocket 연결 해제 함수
    const disconnect = useCallback(() => {
        console.log("Manual disconnect requested");
        isConnectingRef.current = false;
        lastConnectionParamsRef.current = null;

        // 재연결 시도 취소
        cancelReconnect();

        // 하트비트 정지
        stopHeartbeat();

        if (ws.current) {
            ws.current.close(1000, "Manual disconnect");
            ws.current = null;
        }

        setConnectionStatus("Disconnected");
    }, [stopHeartbeat, cancelReconnect]);

    // 수동 재연결 함수
    const reconnect = useCallback(() => {
        console.log("Manual reconnect requested");
        disconnect();
        setTimeout(() => {
            connect();
        }, 1000);
    }, [disconnect, connect]);

    // 컴포넌트 마운트/언마운트 및 의존성 변경 처리
    useEffect(() => {
        // console.log("useWebSocket useEffect triggered", { enabled, url: !!url, token: !!token });

        if (enabled && url && token) {
            const timeoutId = setTimeout(() => {
                connect();
            }, 100);

            return () => {
                clearTimeout(timeoutId);
                // 클린업 시에는 disconnect 호출하지 않음 (의존성 변경 시 재연결 시도 방지)
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url, token, enabled]);

    // 컴포넌트 언마운트 시 하트비트 정지 및 재연결 취소
    useEffect(() => {
        return () => {
            stopHeartbeat();
            cancelReconnect();
        };
    }, [stopHeartbeat, cancelReconnect]);

    // 네트워크 상태 감지 및 자동 재연결
    useEffect(() => {
        const handleOnline = () => {
            console.log("🌐 Network online - checking WebSocket connection...");
            // 네트워크가 다시 연결되었을 때, WebSocket이 연결되어 있지 않으면 재연결 시도
            if (enabled && url && token && (!ws.current || ws.current.readyState !== WebSocket.OPEN)) {
                console.log("🔄 Network restored, attempting to reconnect WebSocket...");
                reconnectAttemptRef.current = 0; // 재연결 시도 횟수 리셋
                // 네트워크 복구 시 즉시 재연결 시도
                if (!isConnectingRef.current) {
                    connect();
                }
            }
        };

        const handleOffline = () => {
            console.log("🌐 Network offline");
        };

        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);

        return () => {
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
        };
    }, [enabled, url, token, connect]);

    return {
        connectionStatus,
        lastMessage,
        error,
        sendMessage,
        reconnect,
        disconnect,
        isConnected: connectionStatus === "Connected",
        isConnecting: connectionStatus === "Connecting",
    };
};

export default useWebSocket;
